import { AcroFormTextField, jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { frenchDate } from './frenchDate';
import { formatHourInput } from './hourInput';
import { absenceReasons } from './timesheetAbsences';
import { primeAmount } from './primeAmount';

const dateLabel = frenchDate;
const BLANK_ROWS = 3;

export function createTimesheetPdf(sheet, { heures = [], primes = [], indemnites = [], clientComment = '', employeeComment = '', hourFormat = 'minutes' } = {}) {
  const pdf = new jsPDF();
  pdf.setFontSize(18);
  pdf.text("Relevé d'heures", 14, 20);
  let y = 28;
  const fieldNames = new Set();
  // Empty cells become fillable text fields so the client can complete the PDF.
  const field = (name, value = '', options = {}) => {
    const text = value == null ? '' : String(value);
    if (text.trim()) return text;
    let unique = name;
    for (let n = 2; fieldNames.has(unique); n += 1) unique = `${name}_${n}`;
    fieldNames.add(unique);
    return { content: '', fieldName: unique, ...options };
  };
  const addField = cell => {
    const textField = new AcroFormTextField();
    textField.fieldName = cell.raw.fieldName;
    textField.Rect = [cell.x + 1, cell.y + 1, cell.width - 2, cell.height - 2];
    textField.fontSize = 10;
    textField.multiline = Boolean(cell.raw.multiline);
    pdf.addField(textField);
  };
  const table = (head, body, options = {}) => {
    autoTable(pdf, { startY: y, margin: { top: 16, bottom: 18, left: 14, right: 14 }, theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3, overflow: 'linebreak', minCellHeight: 9 }, headStyles: { fillColor: [30, 64, 175] },
      rowPageBreak: 'avoid', showHead: 'everyPage', head, body,
      didDrawCell: data => { if (data.section === 'body' && data.cell.raw?.fieldName) addField(data.cell); },
      ...options });
    y = pdf.lastAutoTable.finalY + 8;
  };
  const blankRows = (prefix, columns) => Array.from({ length: BLANK_ROWS }, (_, index) =>
    Array.from({ length: columns }, (_, column) => field(`${prefix}_${index + 1}_${column + 1}`)));

  const contract = sheet.contractNumber
    ? `${sheet.contractNumber}${sheet.amendmentNumber !== '' && sheet.amendmentNumber != null ? `.${sheet.amendmentNumber}` : ''}`
    : '';
  const period = sheet.startDate || sheet.endDate ? `${dateLabel(sheet.startDate)} au ${dateLabel(sheet.endDate)}` : '';
  const week = sheet.weekNumber && sheet.weekNumber !== '—' ? `${sheet.weekNumber}/${sheet.year}` : '';
  table([], [
    ['Intérimaire', field('interimaire', sheet.employeeName)], ['Client', field('client', sheet.clientName)],
    ['Contrat', field('contrat', contract)], ['Période', field('periode', period)], ['Semaine', field('semaine', week)],
    ['Chantier / Service', field('chantier', sheet.projectNumber)],
    ['Fin de mission (oui / non)', field('fin_de_mission', sheet.endOfMission ? 'Oui' : '')],
  ], { columnStyles: { 0: { cellWidth: 52, fontStyle: 'bold' } } });

  const days = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  const hour = value => (Number(value) ? formatHourInput(value, hourFormat) : '');
  table([['Jour', 'Heures de jour', 'Heures de nuit', 'Motif d’absence']], days.map(day => [
    day.charAt(0).toUpperCase() + day.slice(1),
    field(`${day}_jour`, hour(sheet.hours?.[day])),
    field(`${day}_nuit`, hour(sheet.nightHours?.[day])),
    field(`${day}_absence`, absenceReasons.find(([code]) => code === sheet.absences?.[day])?.[1] || sheet.absences?.[day]),
  ]));
  const total = [...Object.values(sheet.hours || {}), ...Object.values(sheet.nightHours || {})].reduce((sum, value) => sum + (Number(value) || 0), 0);
  table([['Total des heures (jour + nuit)']], [[field('total_heures', hour(total))]]);

  const additional = heures.filter(row => String(row.type || '').trim() || Number(row.quantite) > 0);
  table([['Heures diverses', 'Quantité']], [
    ...additional.map((row, index) => [field(`heures_diverses_${index + 1}_type`, row.type), field(`heures_diverses_${index + 1}_quantite`, Number(row.quantite) ? row.quantite : '')]),
    ...blankRows('heures_diverses_ajout', 2),
  ]);
  const rate = value => {
    const amount = primeAmount(value);
    return amount ? `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '';
  };
  const primeRows = (prefix, rows) => [
    ...rows.map((row, index) => [
      field(`${prefix}_${index + 1}_type`, row.type),
      field(`${prefix}_${index + 1}_quantite`, Number(row.quantite) ? row.quantite : ''),
      field(`${prefix}_${index + 1}_taux`, rate(row.montant)),
    ]),
    ...blankRows(`${prefix}_ajout`, 3),
  ];
  table([['Primes', 'Quantité', 'Taux unitaire']], primeRows('prime', primes));
  table([['Indemnités non assujetties', 'Quantité', 'Taux unitaire']], primeRows('indemnite', indemnites));
  table([['Commentaire du client']], [[field('commentaire_client', clientComment, { multiline: true, styles: { minCellHeight: 24 } })]]);
  table([['Commentaire de l’intérimaire']], [[field('commentaire_interimaire', employeeComment, { multiline: true, styles: { minCellHeight: 24 } })]]);
  table([['Nom du signataire', 'Date', 'Signature et cachet du client']], [[
    field('signataire'), field('date_signature'), { content: '', styles: { minCellHeight: 28 } },
  ]]);

  const pages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pages; page++) {
    pdf.setPage(page); pdf.setFontSize(9); pdf.setTextColor(100);
    pdf.text(`Page ${page}/${pages}`, 196, 289, { align: 'right' });
  }
  return pdf;
}
